/**
 * Profile Selector Modal
 *
 * Modal for selecting from saved employee profiles to quickly
 * pre-fill the employee entry form.
 */

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  useProfileListPaginated,
  useDeleteProfile,
} from '@/hooks/nib/useEmployeeProfiles';
import { useDebounce } from '@/hooks/useDebounce';
import { EmployeeProfile } from '@/types/nib/employeeProfile';
import { PAY_INDICATOR_LABELS, CATEGORY_LABELS } from '@/types/nib/manualEntry';
import { SearchHighlight } from '@/components/roster/SearchHighlight';

interface ProfileSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (profile: EmployeeProfile) => void;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  maxSelectable?: number; // undefined = unlimited
  onConfirmBulk?: () => void;
  isBulkLoading?: boolean;
}

export function ProfileSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedIds = [],
  onSelectionChange,
  maxSelectable,
  onConfirmBulk,
  isBulkLoading = false,
}: ProfileSelectorModalProps) {
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  const {
    data: profileData,
    isLoading,
    isError,
  } = useProfileListPaginated({
    search: debouncedSearch || undefined,
  });
  const profiles = profileData?.profiles;
  const totalCount = profileData?.total ?? 0;

  const deleteProfile = useDeleteProfile();

  const isBulkMode = !!onConfirmBulk && !!onSelectionChange;

  const handleToggleSelection = (profileId: string) => {
    if (!onSelectionChange) return;

    const isSelected = selectedIds.includes(profileId);

    if (isSelected) {
      onSelectionChange(selectedIds.filter(id => id !== profileId));
    } else {
      // Check limit before adding
      if (maxSelectable !== undefined && selectedIds.length >= maxSelectable) {
        return; // Can't select more
      }
      onSelectionChange([...selectedIds, profileId]);
    }
  };

  const handleProfileClick = (profile: EmployeeProfile) => {
    if (isBulkMode) {
      handleToggleSelection(String(profile.id));
    } else {
      onSelect(profile);
    }
  };

  const handleDelete = async (
    e: React.MouseEvent,
    profile: EmployeeProfile
  ) => {
    e.stopPropagation(); // Prevent selecting the profile
    if (
      !confirm(`Delete profile for ${profile.first_name} ${profile.last_name}?`)
    ) {
      return;
    }
    setDeletingId(profile.id);
    try {
      await deleteProfile.mutateAsync(String(profile.id));
      toast.success('Profile deleted');
    } catch (err) {
      console.error('Failed to delete profile:', err);
      toast.error('Failed to delete profile');
    } finally {
      setDeletingId(null);
    }
  };

  const warning =
    isBulkMode &&
    maxSelectable !== undefined &&
    selectedIds.length >= maxSelectable
      ? `You can only add ${maxSelectable} more employee${maxSelectable === 1 ? '' : 's'} on your current plan`
      : null;

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const filteredProfiles = profiles ?? [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-selector-title"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2
                  id="profile-selector-title"
                  className="text-xl font-semibold text-gray-900"
                >
                  Select Employee Profile
                </h2>
                {isBulkMode && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedIds.length} selected
                    {maxSelectable !== undefined && (
                      <span className="text-gray-400">
                        {' '}
                        / {maxSelectable} max
                      </span>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Warning Banner */}
            {warning && (
              <div className="mt-3 px-4 py-2 bg-yellow-50 border border-yellow-100 rounded-md">
                <p className="text-sm text-yellow-700">{warning}</p>
              </div>
            )}

            {/* Search */}
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {isLoading ? (
                    <svg
                      className="animate-spin h-5 w-5 text-blue-500"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Search by name, NI number, or nickname..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                  aria-label="Search employee profiles"
                />
              </div>
              {!isLoading && profiles && (
                <p className="mt-1.5 text-xs text-gray-500">
                  {totalCount} employee{totalCount !== 1 ? 's' : ''}
                  {debouncedSearch ? ' found' : ''}
                </p>
              )}
              {isError && (
                <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">
                    Failed to load profiles. Please try again.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Profile List */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-20 bg-gray-200 rounded-lg" />
                  </div>
                ))}
              </div>
            ) : filteredProfiles.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <p className="text-gray-600 font-medium mt-4">
                  {search
                    ? 'No profiles match your search'
                    : 'No saved profiles yet'}
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  {search
                    ? 'Try a different search term'
                    : 'Add employees and check "Save as profile" to reuse them'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredProfiles.map(profile => {
                  const profileId = String(profile.id);
                  const isSelected =
                    isBulkMode && selectedIds.includes(profileId);
                  const isDisabled =
                    isBulkMode &&
                    !isSelected &&
                    maxSelectable !== undefined &&
                    selectedIds.length >= maxSelectable;

                  return (
                    <div
                      key={profile.id}
                      className={`
                        relative border-2 rounded-lg transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2
                        ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-500 hover:bg-blue-50'}
                      `}
                    >
                      <button
                        onClick={() => handleProfileClick(profile)}
                        className="w-full text-left p-4 pr-12 focus:outline-none"
                        disabled={deletingId === profile.id || isDisabled}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox in bulk mode */}
                          {isBulkMode && (
                            <div className="flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                disabled={isDisabled}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between flex-1">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">
                                <SearchHighlight text={profile.first_name} search={debouncedSearch} />{' '}
                                <SearchHighlight text={profile.last_name} search={debouncedSearch} />
                                {profile.nickname && (
                                  <span className="ml-2 text-sm font-normal text-gray-500">
                                    (<SearchHighlight text={profile.nickname} search={debouncedSearch} />)
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                NI: <SearchHighlight text={profile.ni_number} search={debouncedSearch} />
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-600 ml-4">
                              <div className="font-medium">
                                {
                                  PAY_INDICATOR_LABELS[
                                    profile.default_pay_indicator
                                  ]
                                }
                              </div>
                              <div className="text-gray-500 mt-1">
                                {CATEGORY_LABELS[profile.default_category]}
                              </div>
                              <div className="text-gray-500 mt-1">
                                {profile.default_weeks} weeks
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                      {/* Delete button */}
                      <button
                        onClick={e => handleDelete(e, profile)}
                        disabled={deletingId === profile.id}
                        className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                        title="Delete profile"
                        aria-label={`Delete profile for ${profile.first_name} ${profile.last_name}`}
                      >
                        {deletingId === profile.id ? (
                          <svg
                            className="animate-spin w-4 h-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            {isBulkMode ? (
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  disabled={isBulkLoading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirmBulk}
                  disabled={selectedIds.length === 0 || isBulkLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isBulkLoading && (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {isBulkLoading
                    ? 'Adding...'
                    : `Add ${selectedIds.length} Employee${selectedIds.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileSelectorModal;
